import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { inboxApi } from "../api/inbox.js";
import { useStore } from "../store/useStore.js";
export function InboxScreen() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const setUnreadCount = useStore(s => s.setInboxUnread);
    useEffect(() => {
        let cancelled = false;
        inboxApi.list(50)
            .then(r => {
            if (cancelled)
                return;
            setItems(r.items);
            setLoading(false);
            // Mark all unread as read on view, refresh badge
            const unreadIds = r.items.filter(i => !i.read).map(i => i.id);
            if (unreadIds.length > 0) {
                inboxApi.readAll().then(() => {
                    if (cancelled)
                        return;
                    setItems(prev => prev.map(i => ({ ...i, read: true })));
                    setUnreadCount(0);
                }).catch(() => null);
            }
        })
            .catch((e) => {
            if (cancelled)
                return;
            setError(e.message);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [setUnreadCount]);
    if (loading) {
        return (_jsx("div", { style: { padding: 24, textAlign: "center", color: "#475569", fontSize: 13 }, children: "Loading\u2026" }));
    }
    if (error) {
        return (_jsx("div", { style: { padding: 24, textAlign: "center", color: "#ef4444", fontSize: 13 }, children: error }));
    }
    if (items.length === 0) {
        return (_jsxs("div", { style: {
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: "48px 24px", gap: 14, color: "#475569",
            }, children: [_jsx("div", { style: { fontSize: 38, opacity: 0.4 }, children: "\u25C7" }), _jsx("div", { style: { fontSize: 14, fontWeight: 700, letterSpacing: 1, color: "#94a3b8" }, children: "INBOX EMPTY" }), _jsx("div", { style: { fontSize: 11, textAlign: "center", maxWidth: 240, lineHeight: 1.5 }, children: "When someone attacks your squad while you're offline, the result lands here." })] }));
    }
    // Group by day
    const byDay = new Map();
    for (const it of items) {
        const d = new Date(it.createdAt);
        const key = isToday(d) ? "TODAY" : isYesterday(d) ? "YESTERDAY" : formatDay(d);
        const arr = byDay.get(key) ?? [];
        arr.push(it);
        byDay.set(key, arr);
    }
    return (_jsxs("div", { style: { padding: 12, display: "flex", flexDirection: "column", gap: 12 }, children: [_jsxs("div", { style: {
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    padding: "0 4px",
                }, children: [_jsx("span", { style: { color: "#cbd5e1", fontSize: 17, fontWeight: 800, letterSpacing: 1 }, children: "INBOX" }), _jsxs("span", { style: { color: "#475569", fontSize: 11, letterSpacing: 1 }, children: [items.length, " EVENT", items.length === 1 ? "" : "S"] })] }), [...byDay.entries()].map(([day, dayItems]) => (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [_jsx("div", { style: {
                            color: "#475569", fontSize: 9, letterSpacing: 2, fontWeight: 700,
                            padding: "4px 4px 0",
                        }, children: day }), dayItems.map(it => (_jsx(InboxRow, { item: it }, it.id)))] }, day)))] }));
}
function InboxRow({ item }) {
    const isWin = item.outcome === "win";
    const isDraw = item.outcome === "draw";
    const color = isWin ? "#22c55e" : isDraw ? "#f59e0b" : "#ef4444";
    // Per kind: defense = "DEFENDED" (won) / "FELL" (lost) — your squad was attacked while offline
    // offense = "RAID" — you attacked someone
    const kindBadge = item.kind === "defense"
        ? (isWin ? "DEFENDED" : isDraw ? "STALEMATE" : "BREACHED")
        : "RAID";
    const kindColor = item.kind === "defense"
        ? (isWin ? "#22c55e" : isDraw ? "#f59e0b" : "#ef4444")
        : "#22d3ee";
    const oppName = item.opponent.firstName + (item.opponent.isBot ? " 🤖" : "");
    const time = formatTime(new Date(item.createdAt));
    return (_jsxs("div", { style: {
            background: item.read ? "#0f0f17" : "#12121a",
            border: `1px solid ${item.read ? "#1e1e2e" : color + "44"}`,
            borderRadius: 10,
            padding: "10px 12px",
            display: "flex", flexDirection: "column", gap: 8,
            position: "relative",
            boxShadow: item.read ? "none" : `0 0 12px ${color}14`,
        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [_jsx("span", { style: {
                                    color: kindColor, fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
                                    padding: "2px 6px", borderRadius: 3,
                                    background: `${kindColor}18`, border: `1px solid ${kindColor}40`,
                                }, children: kindBadge }), _jsxs("span", { style: { color, fontSize: 11, fontWeight: 700, letterSpacing: 1 }, children: [item.outcome.toUpperCase(), item.ratingDelta !== 0 && (_jsxs("span", { style: {
                                            color: item.ratingDelta > 0 ? "#22c55e" : "#ef4444",
                                            marginLeft: 6, fontSize: 10, fontVariantNumeric: "tabular-nums",
                                        }, children: [item.ratingDelta > 0 ? "+" : "", item.ratingDelta] }))] })] }), _jsx("span", { style: { color: "#475569", fontSize: 10, fontVariantNumeric: "tabular-nums" }, children: time })] }), _jsx("div", { style: { display: "flex", alignItems: "center", gap: 10 }, children: _jsxs("span", { style: {
                        color: item.opponent.isBot ? "#94a3b8" : "#cbd5e1",
                        fontSize: 13, fontWeight: 700,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        flex: 1, minWidth: 0,
                    }, children: [item.kind === "defense" ? "ATTACKED BY " : "RAIDED ", _jsx("span", { style: { color: "#fff" }, children: oppName }), item.opponent.rating > 0 && (_jsxs("span", { style: { color: "#475569", fontSize: 10, marginLeft: 6 }, children: [item.opponent.rating, "E"] }))] }) }), _jsxs("div", { style: { display: "flex", gap: 8, alignItems: "center" }, children: [_jsx(HpStrip, { label: "YOU", pct: item.myHpPct, color: "#7c3aed" }), _jsx("span", { style: { color: "#334155", fontSize: 9, letterSpacing: 1 }, children: "VS" }), _jsx(HpStrip, { label: "OPP", pct: item.oppHpPct, color: "#ef4444", right: true })] })] }));
}
function HpStrip({ label, pct, color, right }) {
    return (_jsxs("div", { style: { flex: 1, display: "flex", flexDirection: "column", gap: 3, alignItems: right ? "flex-end" : "flex-start" }, children: [_jsxs("div", { style: { display: "flex", gap: 6, alignItems: "baseline" }, children: [_jsx("span", { style: { color: "#475569", fontSize: 9, letterSpacing: 1.2 }, children: label }), _jsxs("span", { style: { color: pct > 0 ? color : "#475569", fontSize: 10, fontWeight: 700, fontVariantNumeric: "tabular-nums" }, children: [pct, "%"] })] }), _jsx("div", { style: { width: "100%", background: "#0a0a0f", borderRadius: 2, height: 4, overflow: "hidden" }, children: _jsx("div", { style: {
                        width: `${pct}%`, height: "100%",
                        background: pct > 0 ? color : "#1e293b",
                        borderRadius: 2,
                        float: right ? "right" : undefined,
                        boxShadow: pct > 50 ? `0 0 4px ${color}66 inset` : "none",
                        transition: "width 0.5s ease-out",
                    } }) })] }));
}
// ─── Date helpers ─────────────────────────────────────────────────────────────
function isToday(d) {
    const now = new Date();
    return d.toDateString() === now.toDateString();
}
function isYesterday(d) {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return d.toDateString() === y.toDateString();
}
function formatDay(d) {
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
function formatTime(d) {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
